import AltRouteIcon from '@mui/icons-material/AltRoute';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GroupsIcon from '@mui/icons-material/Groups';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MenuIcon from '@mui/icons-material/Menu';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { ErrorBoundary } from './ErrorBoundary';
import { Logo } from './Logo';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeSwitcher } from './ThemeSwitcher';

const DRAWER_WIDTH = 240;

/**
 * Navigation entries. `enabled` marks what is already built; the rest are
 * visible but inert so the shape of the panel is clear from phase 0 onwards.
 */
const NAV_ITEMS = [
  { key: 'dashboard', path: '/', enabled: true, Icon: SpaceDashboardIcon },
  { key: 'products', path: '/products', enabled: true, Icon: Inventory2Icon },
  { key: 'warehouses', path: '/warehouses', enabled: true, Icon: WarehouseIcon },
  { key: 'vehicles', path: '/vehicles', enabled: true, Icon: LocalShippingIcon },
  { key: 'stock', path: '/stock', enabled: true, Icon: StackedBarChartIcon },
  { key: 'customers', path: '/customers', enabled: true, Icon: GroupsIcon },
  { key: 'routes', path: '/routes', enabled: true, Icon: AltRouteIcon },
  { key: 'live', path: '/live', enabled: true, Icon: MyLocationIcon },
  { key: 'documents', path: '/documents', enabled: true, Icon: ReceiptLongIcon },
  { key: 'payments', path: '/payments', enabled: true, Icon: PaymentsIcon },
  { key: 'users', path: '/users', enabled: true, Icon: ManageAccountsIcon },
  { key: 'reports', path: '/reports', enabled: true, Icon: AssessmentIcon },
  { key: 'settings', path: '/settings', enabled: true, Icon: SettingsIcon },
] as const;

/** Two letters for the avatar; a name is not always two words. */
function initialsOf(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
}

export function AppShell() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const location = useLocation();

  /**
   * Below `md` the drawer slides over the content instead of permanently taking
   * 240 px of it. In a narrow window the permanent drawer left the main area
   * barely a hundred pixels wide, which made the map and the tables unusable.
   */
  const permanent = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigation = (
    <>
      {/* Clears the app bar, which is drawn over the drawer. */}
      <Toolbar />
      <List sx={{ py: 1.5, flexGrow: 1 }}>
        {NAV_ITEMS.map((item) => (
          <ListItemButton
            key={item.key}
            component={item.enabled ? NavLink : 'div'}
            to={item.enabled ? item.path : undefined}
            disabled={!item.enabled}
            // A nested route such as /routes/:id keeps its parent highlighted.
            selected={
              item.enabled &&
              (item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path))
            }
            onClick={() => setMobileOpen(false)}
          >
            <ListItemIcon>
              <item.Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t(`nav.${item.key}`)} />
          </ListItemButton>
        ))}
      </List>

      {/* Who is signed in, at the foot of the sidebar rather than squeezed
          into the bar, where it survives a narrow window. */}
      {user && (
        <>
          <Divider />
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 2 }}>
            <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: '0.8125rem' }}>
              {initialsOf(user.firstName, user.lastName)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {t(`roles.${user.role}`)}
              </Typography>
            </Box>
          </Stack>
        </>
      )}
    </>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <AppBar position="fixed" sx={{ zIndex: (th) => th.zIndex.drawer + 1 }}>
        <Toolbar>
          {!permanent && (
            <IconButton
              color="inherit"
              onClick={() => setMobileOpen((open) => !open)}
              aria-label={t('common.menu')}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* The bar is white now, which is the surface the mark was drawn
              for, so it goes here rather than in the sidebar. */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1 }}>
            <Logo height={28} />
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: { xs: 'none', md: 'block' } }}
              noWrap
            >
              {t('app.name')}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <LanguageSwitcher />
            <ThemeSwitcher />
            <Tooltip title={t('common.logout')}>
              <IconButton
                color="inherit"
                onClick={() => void logout()}
                aria-label={t('common.logout')}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={permanent ? 'permanent' : 'temporary'}
        open={permanent || mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: permanent ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          // A column, so the signed-in block can sit at the foot of it.
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {navigation}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          // Without this, a wide child (a table, a map) pushes the flex item
          // past the viewport instead of shrinking inside it.
          minWidth: 0,
          width: permanent ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
        }}
      >
        <Toolbar />
        {/* Keyed on the path so leaving a broken screen clears the error. */}
        <ErrorBoundary resetKey={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </Box>
    </Box>
  );
}
